import React from 'react';
import { MarkerF } from '@react-google-maps/api';
import { useColorModeValue } from '@chakra-ui/react'; // Import useColorModeValue

const getMarkerIcon = (site, isMainMap) => {
  // Only create Point objects if google.maps is available
  if (!window.google || !window.google.maps) {
    return null;
  }

  // Base icon properties
  const iconProps = {
    url: '/marker-main.svg',
    scaledSize: new window.google.maps.Size(40, 50),
    anchor: new window.google.maps.Point(20, 50),
    labelOrigin: new window.google.maps.Point(20, -10)
  };

  // On main map page, use circle instead of pin
  if (isMainMap) {
    iconProps.url = '/marker-main.svg';
    iconProps.scaledSize = new window.google.maps.Size(40, 40);
    iconProps.anchor = new window.google.maps.Point(20, 20);
  }

  // Increase size for sites with multiple features
  const featureCount = site?.features ? Object.values(site.features).filter(Boolean).length : 0;
  if (featureCount > 3) {
    const { width, height } = iconProps.scaledSize;
    iconProps.scaledSize = new window.google.maps.Size(width * 1.2, height * 1.2);
  }

  return iconProps;
};

const SiteMarker = ({ site, onClick, clusterer, showLabels, isMainMap = false }) => {
  const icon = React.useMemo(() => getMarkerIcon(site, isMainMap), [site, isMainMap]);
  // Get the appropriate label color based on the theme mode
  const labelColor = useColorModeValue(
    '#3B2E1E', // Light mode: map.text (Cartographer Ink) or black '#000000'
    '#FFFFFF'  // Dark mode: White for high contrast
  );

  if (!icon) {
    return null; // Don't render marker if icon couldn't be created
  }
  
  // Check if site has valid coordinates - handle both lat/lng and latitude/longitude formats
  if (!site?.location?.coordinates) {
    console.error('[SiteMarker] Site has invalid or missing coordinates:', site?.id, site?.location?.coordinates);
    return null;
  }
  
  const coords = site.location.coordinates;
  const hasLat = coords.lat !== undefined || coords.latitude !== undefined;
  const hasLng = coords.lng !== undefined || coords.longitude !== undefined || coords.lon !== undefined; // Added lon check
  
  if (!hasLat || !hasLng) {
    console.error('[SiteMarker] Site has invalid or missing coordinates (after lon check):', site?.id, coords);
    return null;
  }

  // Normalize coordinates to handle both formats
  const lat = site.location.coordinates.lat !== undefined 
    ? Number(site.location.coordinates.lat) 
    : Number(site.location.coordinates.latitude);
  
  const lng = site.location.coordinates.lng !== undefined 
    ? Number(site.location.coordinates.lng) 
    : (site.location.coordinates.longitude !== undefined 
        ? Number(site.location.coordinates.longitude) 
        : Number(site.location.coordinates.lon)); // Added lon handling

  return (
    <MarkerF
      position={{ lat, lng }}
      onClick={() => onClick(site)}
      icon={icon}
      clusterer={clusterer}
      title={site.name}
      label={showLabels ? {
        text: site.name,
        color: labelColor, // Use the dynamic label color
        fontSize: '13px',
        fontWeight: 'bold',
        className: 'marker-label' // Keep class for potential CSS overrides
      } : null}
    />
  );
};

export default React.memo(SiteMarker);
